import useRequest from "@/lib/request/useRequest";
import { Field } from "@/types/field";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Checkbox, Chip, Input } from "@heroui/react";
import { useMemo, useState } from "react";

interface FieldMultiSelectProps {
    selectedFieldIds: number[];
    onChange: (ids: number[]) => void;
}

function FieldMultiSelect({ selectedFieldIds, onChange }: FieldMultiSelectProps) {
    const { useGetRequest } = useRequest();
    const [search, setSearch] = useState("");

    const fieldsQuery = useGetRequest<Field[]>({ url: "fields/" });

    const filtered = useMemo(() => {
        const all = fieldsQuery.data ?? [];
        if (!search.trim()) return all;
        const lower = search.toLowerCase();
        return all.filter((f) => f.name.toLowerCase().includes(lower));
    }, [fieldsQuery.data, search]);

    const selectedSet = useMemo(() => new Set(selectedFieldIds), [selectedFieldIds]);

    const toggle = (id: number, checked: boolean) => {
        if (checked) {
            onChange([...selectedFieldIds, id]);
        } else {
            onChange(selectedFieldIds.filter((fid) => fid !== id));
        }
    };

    const selectedFields = useMemo(
        () => (fieldsQuery.data ?? []).filter((f) => selectedSet.has(f.id)),
        [fieldsQuery.data, selectedSet],
    );

    return (
        <div className="flex flex-col gap-2">
            <Input
                size="sm"
                placeholder="Search fields..."
                value={search}
                onValueChange={setSearch}
                startContent={<FontAwesomeIcon icon={faSearch} className="text-default-400" />}
                isClearable
                onClear={() => setSearch("")}
            />

            {selectedFields.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {selectedFields.map((f) => (
                        <Chip
                            key={f.id}
                            size="sm"
                            onClose={() => toggle(f.id, false)}
                            variant="flat"
                            color="primary"
                        >
                            {f.name}
                        </Chip>
                    ))}
                </div>
            )}

            <div className="flex flex-col gap-1 max-h-48 overflow-y-auto border rounded-medium p-2">
                {fieldsQuery.isLoading && (
                    <p className="text-sm text-default-400">Loading fields...</p>
                )}
                {filtered.map((f) => (
                    <Checkbox
                        key={f.id}
                        size="sm"
                        isSelected={selectedSet.has(f.id)}
                        onValueChange={(checked) => toggle(f.id, checked)}
                    >
                        {f.name}
                    </Checkbox>
                ))}
                {!fieldsQuery.isLoading && filtered.length === 0 && (
                    <p className="text-sm text-default-400">No fields found.</p>
                )}
            </div>
        </div>
    );
}

export default FieldMultiSelect;
